import React from "react";
import { Link } from "react-router-dom";
import api from "@services/api";
import "./driver-assign.css";

export default function DriverAreasPage() {
  const [drivers, setDrivers] = React.useState([]);
  const [zones, setZones] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [savingId, setSavingId] = React.useState(null);
  const [msg, setMsg] = React.useState(null);

  const load = React.useCallback(() => {
    setLoading(true);
    Promise.all([api.get("/assignments/drivers"), api.get("/map-zones")])
      .then(([d, z]) => {
        setZones(z.data?.data || {});
        setDrivers((d.data?.data || []).map((dr) => ({
          ...dr,
          zoneSet: new Set((dr.areas || []).map((a) => a.zone_key)),
          caps: Object.fromEntries((dr.areas || []).map((a) => [a.zone_key, a.max_per_slot ?? ""])),
        })));
      })
      .finally(() => setLoading(false));
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const toggleZone = (di, zk) =>
    setDrivers((ds) => ds.map((d, i) => {
      if (i !== di) return d;
      const s = new Set(d.zoneSet);
      s.has(zk) ? s.delete(zk) : s.add(zk);
      return { ...d, zoneSet: s };
    }));
  const setCap = (di, zk, v) =>
    setDrivers((ds) => ds.map((d, i) => (i === di ? { ...d, caps: { ...d.caps, [zk]: v } } : d)));

  const save = async (di) => {
    const d = drivers[di];
    setSavingId(d.driver_id);
    setMsg(null);
    try {
      const areas = [...d.zoneSet].map((zk) => ({
        zone_key: zk,
        max_per_slot: d.caps[zk] === "" || d.caps[zk] == null ? null : Number(d.caps[zk]),
      }));
      await api.put(`/assignments/drivers/${d.driver_id}/areas`, { areas });
      setMsg({ type: "ok", text: `Saved coverage for ${d.name}.` });
    } catch (e) {
      setMsg({ type: "err", text: e?.response?.data?.message || "Failed to save." });
    } finally {
      setSavingId(null);
    }
  };

  const zoneList = Object.entries(zones);

  return (
    <div className="oc-content da-page">
      <div className="da-head">
        <div>
          <h2>Driver Areas</h2>
          <p className="da-muted">Choose which map areas each driver covers (and an optional max orders per timeslot). Smart Assign uses this to route orders.</p>
        </div>
        <Link className="oc-btn" to="/logistics/smart-assign">Smart Assign →</Link>
      </div>

      {msg && <div className={`da-msg ${msg.type}`}>{msg.text}</div>}
      {loading && <div className="da-muted">Loading…</div>}

      {!loading && drivers.length === 0 && <div className="oc-card">No active drivers.</div>}

      {!loading && drivers.map((d, di) => (
        <div className="da-card" key={d.driver_id}>
          <div className="da-driver-head">
            <strong>{d.name}</strong> <span className="da-muted">#{d.driver_id} · {d.telephone || "—"}</span>
            <div className="da-spacer" />
            <button className="oc-btn primary" onClick={() => save(di)} disabled={savingId === d.driver_id}>
              {savingId === d.driver_id ? "Saving…" : "Save"}
            </button>
          </div>
          <div className="da-zone-grid">
            {zoneList.map(([name, z]) => {
              const on = d.zoneSet.has(name);
              return (
                <div key={name} className={"da-zone" + (on ? " on" : "")}>
                  <label className="da-zone-lbl">
                    <input type="checkbox" checked={on} onChange={() => toggleZone(di, name)} />
                    <span className="da-dot" style={{ background: z.color }} />
                    {z.name_ar || name.replace(/_/g, " ")}
                  </label>
                  {on && (
                    <input
                      className="oc-input da-cap"
                      type="number" min="1"
                      value={d.caps[name] ?? ""}
                      onChange={(e) => setCap(di, name, e.target.value)}
                      placeholder="max/slot"
                      title="Max orders per timeslot (optional)"
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
